import { CurrentUserService } from '@activia/cm-api';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { DashboardFacade } from './dashboard.facade';
import { IDashboardWidget } from './dashboard-widget.interface';
import * as DashboardAction from './dashboard.action';
import { catchError, exhaustMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { ErrorHandlingService } from '@amp/error';
import { getUserPreferencesByKey, updateUserPreferencesByKey } from '@amp/utils/common';

@Injectable()
export class DashboardEffects {
  private DASHBOARD_WIDGETS_PREFERENCE_KEY = 'dashboard.widgets';

  /**
   * Init the dashboard
   */

  dashboardInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardAction.DashboardInit),
      exhaustMap((_) =>
        getUserPreferencesByKey<any>(this.currentUserService, this.DASHBOARD_WIDGETS_PREFERENCE_KEY, undefined, false).pipe(
          map((preferences) => {
            // parse the dashboard preferences
            const dashboardPreferences = preferences;
            return DashboardAction.DashboardInitSuccess({ widgetsSettings: dashboardPreferences });
          }),
          catchError((err) => {
            if (err.status !== 404) {
              this.errorHandlingService.catchError(err, undefined, 'dashboardScope.dashboard.error.init-fail');
            }
            return of(DashboardAction.DashboardInitFail({ error: err }));
          })
        )
      )
    )
  );

  /**
   * Update the widget settings
   */

  dashboardWidgetSettingsUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardAction.DashboardWidgetSettingsUpdate),
      // get the widgets preferences
      withLatestFrom(this.dashboardFacade.widgets$),
      // make call to api
      switchMap(([action, widgets]) => {
        const newWidgetPreferences: IDashboardWidget = action.widgetSettings;

        // remove any previous preference for that widget
        widgets = widgets.filter((widgetSettings) => widgetSettings.name !== newWidgetPreferences.name);
        widgets.push(newWidgetPreferences);

        return updateUserPreferencesByKey(this.currentUserService, this.DASHBOARD_WIDGETS_PREFERENCE_KEY, widgets).pipe(
          map((_) => DashboardAction.DashboardWidgetSettingsUpdateSuccess({ widgetsSettings: widgets })),
          catchError((err) => {
            this.errorHandlingService.catchError(err, undefined, 'dashboardScope.dashboard.error.widget-settings-update-fail');
            return of(DashboardAction.DashboardWidgetSettingsUpdateFail({ error: err }));
          })
        );
      })
    )
  );

  /**
   * Update the widget list settings
   */

  dashboardWidgetSettingsUpdateList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardAction.DashboardWidgetSettingsListUpdate),
      // make call to api
      switchMap(({ widgetsSettings }) => updateUserPreferencesByKey(this.currentUserService, this.DASHBOARD_WIDGETS_PREFERENCE_KEY, widgetsSettings).pipe(
          map((_) => DashboardAction.DashboardWidgetSettingsListUpdateSuccess({ widgetsSettings })),
          catchError((err) => {
            this.errorHandlingService.catchError(err, undefined, 'dashboardScope.dashboard.error.widget-settings-list-update-fail');
            return of(DashboardAction.DashboardWidgetSettingsListUpdateFail({ error: err }));
          })
        ))
    )
  );

  constructor(private actions$: Actions, private dashboardFacade: DashboardFacade, private currentUserService: CurrentUserService, private errorHandlingService: ErrorHandlingService) {}
}
